import { DocumentServicesObject } from '@wix/document-services-types';
import {
  createUnifiedComponentsInstallationError,
  UnifiedComponentsInstallationErrorCode as ErrorType,
  UnifiedComponentsInstallationError,
} from './errors';
import { chainPromisesAsync, applyPresetsToAppComponents } from './utils';
import { getBIReporter } from './bi';
import { AddAppData, AppData } from '../../../../types/unifiedComponents';
import {
  InstallOptions,
  InstallCallbacks,
} from '@wix/editor-platform-host-integration-apis';
import { ComponentType } from '@wix/ambassador-devcenter-components-v1-component/types';
import { PlatformContext } from '../../../../types/platformApi';
import { installSingleUnifiedComponentsApp } from './installSingleApp';
import { LEGACY_EDITOR_COMPONENT_TYPES } from './constants';
import { autoPopulatePlugins } from './addPlugins/autoPopulatePlugins';
import { autoPopulateHeaderPlugins } from './addPlugins/autoPopulateHeaderPlugins';
import { isGuid } from '../../../utils/guid';

const HYBRID_INSTALLATION_EXPERIMENT =
  'specs.editor-platform.allowHybridInstallation';

const unifiedComponentTypes = [
  ComponentType.EDITOR_REACT_COMPONENT,
  ComponentType.UNIFIED_LIGHTBOX,
  ComponentType.UNIFIED_PAGE,
];

function triggerAppMarketDataFetchForAppsToInstall(
  documentServicesAPI: DocumentServicesObject,
  appDefIds: string[],
) {
  appDefIds.forEach((appDefId) => {
    if (!isGuid(appDefId)) {
      return;
    }
    documentServicesAPI.tpa.appMarket.getData(appDefId);
  });
}

function isUnifiedComponentsApp(appData: AppData) {
  const unifiedComponent = (appData?.components ?? []).filter(
    ({ data, type }) =>
      type !== ComponentType.WIDGET_PLUGIN &&
      data &&
      ('installation' in data ||
        unifiedComponentTypes.includes(type as ComponentType)),
  );
  return unifiedComponent.length > 0;
}

export function containsLegacyEditorComponents(appData: AppData) {
  return (appData?.components ?? []).some(({ type }) =>
    LEGACY_EDITOR_COMPONENT_TYPES.includes(type as ComponentType),
  );
}

export function isEmptyLegacyEditorComponents(appData: AppData) {
  return !containsLegacyEditorComponents(appData);
}

function hasLegacyNonUnifiedComponents(appData: AppData): boolean {
  return (appData?.components ?? []).some(
    ({ data, type }) =>
      type !== ComponentType.WIDGET_PLUGIN &&
      LEGACY_EDITOR_COMPONENT_TYPES.includes(type as ComponentType) &&
      !(data && 'installation' in data) &&
      !unifiedComponentTypes.includes(type as ComponentType),
  );
}

function installAppsInternals(
  context: PlatformContext,
  documentServices: DocumentServicesObject,
  appDefIds: string[],
  installOptions: InstallOptions,
  callbacks?: InstallCallbacks,
): Promise<void> {
  const { appsOptions, ...otherOptions } =
    installOptions ?? ({} as InstallOptions);
  const biReporter = getBIReporter(context, appDefIds, appsOptions);

  function triggerFinishInstallAllAppsCallback(
    installedAppsData: AddAppData[],
  ) {
    return new Promise<void>((resolve) => {
      callbacks?.finishAllCallback?.(installedAppsData);
      resolve();
    });
  }

  async function handlePluginsAutoPopulate(appData: AppData): Promise<void> {
    // In the hybrid flow the unified leg (installSingleUnifiedComponentsApp)
    // already auto-populates header and generic plugins from the full component
    // list, so re-running it here would populate every plugin twice (and header
    // plugin installation is not idempotent). When the hybrid experiment is
    // enabled, installNonUnifiedApp is only ever reached as the secondary leg of
    // installHybridApp — the standalone non-unified branch is gated behind the
    // experiment being disabled — so skipping here keeps population at exactly once.
    if (context.experimentsAPI.enabled(HYBRID_INSTALLATION_EXPERIMENT)) {
      return;
    }

    const appOptions = appsOptions?.[appData.appDefinitionId] || {};
    const extendedContext = {
      ...context,
      documentServices,
    };

    await autoPopulateHeaderPlugins(
      extendedContext,
      appOptions,
      appData.appDefinitionId,
      appData.components,
    );

    await autoPopulatePlugins(
      extendedContext,
      appOptions,
      appData.appDefinitionId,
      appData.components,
    );
  }

  function installNonUnifiedApp(appData: AppData) {
    return new Promise<AddAppData>((resolve, reject) => {
      documentServices.platform.installationFlow.addAppToDocument(
        appData.appDefinitionId,
        {
          ...(appsOptions?.[appData.appDefinitionId]
            ? {
                [appData.appDefinitionId]: appsOptions[appData.appDefinitionId],
              }
            : {}),
          ...otherOptions,
          singleAppCallback: (_appDefId: string, addedAppData: AddAppData) => {
            handlePluginsAutoPopulate(appData)
              .then(() => {
                applyPresetsToAppComponents(
                  context,
                  documentServices,
                  appData.appDefinitionId,
                );
                callbacks?.singleAppCallback?.(
                  appData.appDefinitionId,
                  addedAppData,
                );
                resolve(addedAppData);
              })
              .catch(reject);
          },
          onError: (error: Error, _errName: string, appDefinitionId: string) =>
            reject(
              createUnifiedComponentsInstallationError(
                ErrorType.nonUnifiedInstallationFailed,
                'Error occur in non-unified applications installation process',
              )
                .withAppDefIds(appDefinitionId)
                .withParentError(error as Error),
            ),
        },
      );
    });
  }

  async function installHybridApp(appData: AppData): Promise<AddAppData> {
    const appOptions = appsOptions?.[appData.appDefinitionId];
    const hasLegacyComponents = !appData.isEmptyLegacyEditorComponents;
    const skipEditorScript = hasLegacyNonUnifiedComponents(appData);

    const unifiedAddAppData = await installSingleUnifiedComponentsApp(
      { ...context, documentServices },
      appData,
      biReporter,
      appOptions,
      skipEditorScript ? undefined : callbacks?.singleAppCallback,
      skipEditorScript,
    );

    if (hasLegacyComponents) {
      await installNonUnifiedApp(appData);
    }

    return unifiedAddAppData;
  }

  function installAllAppsByClassification(appsData: AppData[]) {
    const installedAppsData: AddAppData[] = [];

    const shouldSkipInstallation = (appData: AppData) =>
      context.experimentsAPI.enabled(HYBRID_INSTALLATION_EXPERIMENT)
        ? appData.appType === 'Dashboard'
        : !appData.isUnifiedComponentsApp &&
          appData.isEmptyLegacyEditorComponents;

    const updateAppDataAfterInstallation = (appData: AppData) => {
      if (shouldSkipInstallation(appData)) {
        const skippedData = { ...appData, success: true } as AddAppData;
        callbacks?.singleAppCallback?.(appData.appDefinitionId, skippedData);
        installedAppsData.push(skippedData);
        return Promise.resolve();
      }

      return biReporter.withBI(
        'UNIFIED_APP_INSTALL',
        { appDefinitionId: appData.appDefinitionId },
        () => {
          if (context.experimentsAPI.enabled(HYBRID_INSTALLATION_EXPERIMENT)) {
            return installHybridApp(appData).then((addedAppData) =>
              installedAppsData.push(addedAppData),
            );
          } else if (appData.isUnifiedComponentsApp) {
            return installSingleUnifiedComponentsApp(
              { ...context, documentServices },
              appData,
              biReporter,
              appsOptions?.[appData.appDefinitionId],
              callbacks?.singleAppCallback,
            ).then((addedAppData) => installedAppsData.push(addedAppData));
          } else {
            return installNonUnifiedApp(appData).then((addedAppData) =>
              installedAppsData.push(addedAppData),
            );
          }
        },
      );
    };

    return new Promise<AddAppData[]>((resolve, reject) =>
      chainPromisesAsync(appsData, updateAppDataAfterInstallation)
        .then(() => resolve(installedAppsData))
        .catch(reject),
    );
  }

  function validateIfNoAppProvisioned(provisionedApps: AppData[]) {
    return new Promise<AppData[]>((resolve, reject) => {
      if (!provisionedApps || provisionedApps.length === 0) {
        reject(
          createUnifiedComponentsInstallationError(
            ErrorType.noAppRecivedFromProvision,
            'None of the apps could be provisioned',
          ).withAppDefIds(appDefIds),
        );
      }
      resolve(provisionedApps);
    });
  }

  function processAppComponentsAndSetManifests(provisionedApps: AppData[]) {
    return new Promise<AppData[]>((resolve, reject) => {
      const appsWithComponents = provisionedApps.filter(
        (appData) => appData.components && appData.components.length > 0,
      );

      if (appsWithComponents.length === 0) {
        return resolve(provisionedApps);
      }

      const loadPromises = appsWithComponents.map((appData) => {
        return context.componentsPlatformService.loadAppManifestAndPanels(
          // @ts-expect-error DM types mismatch
          // TODO: decide what types should be used here AppData vs EditorClientSpecMapEntry!!!
          appData,
        );
      });

      Promise.all(loadPromises)
        .then(() => resolve(provisionedApps))
        .catch((error: Error) =>
          reject(
            createUnifiedComponentsInstallationError(
              ErrorType.failedToLoadHandleManifests,
              'Failed to process components and set manifests',
            )
              .withAppDefIds(appDefIds)
              .withParentError(error),
          ),
        );
    });
  }

  function classifyProvisionedApps(appsData: AppData[]) {
    return new Promise<AppData[]>((resolve) =>
      resolve(
        appsData.map((appData: AppData) => {
          return {
            ...appData,
            isUnifiedComponentsApp: isUnifiedComponentsApp(appData),
            isEmptyLegacyEditorComponents:
              isEmptyLegacyEditorComponents(appData),
          };
        }),
      ),
    );
  }

  function provisionApps(): Promise<AppData[]> {
    return biReporter.withBI(
      'PROVISION_APPS',
      {},
      function provisionAndClassifyApps() {
        return new Promise<AppData[]>((resolve, reject) => {
          documentServices.platform.installationFlow
            .provisionApps(appDefIds, appsOptions ?? {})
            .then((appsData) => resolve(appsData as AppData[]))
            .catch((error: Error) =>
              reject(
                createUnifiedComponentsInstallationError(
                  ErrorType.provisionAppsFailed,
                  'Could not provision apps',
                )
                  .withAppDefIds(appDefIds)
                  .withParentError(error),
              ),
            );
        });
      },
    );
  }

  function installAllApps() {
    return biReporter.withBI(
      'PLATFORM_INSTALLATION',
      {},
      () =>
        new Promise<void>((resolve, reject) => {
          provisionApps()
            .then(classifyProvisionedApps)
            .then(validateIfNoAppProvisioned)
            .then(processAppComponentsAndSetManifests)
            .then(installAllAppsByClassification)
            .then(triggerFinishInstallAllAppsCallback)
            .then(resolve)
            .catch((error: UnifiedComponentsInstallationError) => {
              callbacks?.onError?.(
                error,
                error.name,
                error.appDefIds ?? appDefIds.toString(),
              );
              reject(error);
            });
        }),
    );
  }

  return installAllApps();
}

let installationLevel = 0;

const createSetAutosave = (context: PlatformContext) => (enabled: boolean) => {
  context.platformApiMethods.editor.setAutosave({} as AppData, '', enabled);
};

export async function installApps(
  context: PlatformContext,
  documentServicesAPI: DocumentServicesObject,
  appDefIds: string[],
  options: InstallOptions,
  callbacks?: InstallCallbacks,
): Promise<void> {
  const setAutosave = createSetAutosave(context);

  let shouldEnableAutosave = false;
  if (installationLevel === 0) {
    shouldEnableAutosave = true;
    setAutosave(false);
  }

  installationLevel += 1;

  try {
    triggerAppMarketDataFetchForAppsToInstall(documentServicesAPI, appDefIds);

    return await installAppsInternals(
      context,
      documentServicesAPI,
      appDefIds,
      options,
      callbacks,
    );
  } catch (err) {
    // removing explicit error re-throw breaks errors handling in tests
    throw err;
  } finally {
    installationLevel -= 1;

    if (shouldEnableAutosave) {
      shouldEnableAutosave = false;
      setAutosave(true);

      if (
        !options?.noForcedSaveAfterInstallationCompleted &&
        !documentServicesAPI.transactions.isRunningAny()
      ) {
        context.platformApiMethods.editor.saveInBackground();
      }
    }
  }
}
